import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink } from 'react-router-dom';

// Responsive Navigation Item Component
function NavItem({ to, children, disabled = false, className = '', onMouseEnter, onFocus, onClick, ...rest }) {
  if (disabled) {
    return (
      <span
        className={`px-3 py-2 rounded-lg text-sm font-medium text-gray-400 cursor-not-allowed opacity-50 whitespace-nowrap ${className}`}
        {...rest}
      >
        {children}
      </span>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
          isActive
            ? 'bg-green-100 text-green-700 font-semibold'
            : 'text-gray-700 hover:bg-green-50 hover:text-green-600'
        } ${className}`
      }
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      onClick={onClick}
      {...rest}
    >
      {children}
    </NavLink>
  );
}

// Main Responsive Navigation Component
export default function ResponsiveNavigation({ 
  children, 
  className = '', 
  maxWidth = 'calc(100vw - 400px)' 
}) {
  const [visibleItems, setVisibleItems] = useState([]);
  const [overflowItems, setOverflowItems] = useState([]);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const navRef = useRef(null);
  const overflowMenuRef = useRef(null);

  // Initialize children and prioritize certain items
  const allItems = React.useMemo(() => {
    const items = Array.isArray(children) ? children.filter(Boolean) : [children].filter(Boolean);
    
    // Reorder items to prioritize "Stores" over "Dashboard"
    const prioritizedItems = [];
    const storesItem = items.find(item => 
      item.props && item.props.children && 
      (item.props.children.includes('Stores') || item.props.children.includes('🛍️'))
    );
    const dashboardItem = items.find(item => 
      item.props && item.props.children && 
      item.props.children.includes('Dashboard')
    );
    
    // Add other items first (Home, etc.)
    items.forEach(item => {
      if (item !== storesItem && item !== dashboardItem) {
        prioritizedItems.push(item);
      }
    });
    
    // Add Stores before Dashboard
    if (storesItem) prioritizedItems.push(storesItem);
    if (dashboardItem) prioritizedItems.push(dashboardItem);
    
    return prioritizedItems;
  }, [children]);

  // Simple calculation - show all items that fit
  const calculateVisibleItems = useCallback(() => {
    if (!navRef.current || allItems.length === 0) return;

    const navWidth = navRef.current.offsetWidth;
    const moreButtonWidth = 80; // Approximate width of "More" button
    const itemWidth = 120; // Approximate width per item
    const availableWidth = navWidth - moreButtonWidth;
    
    const maxVisible = Math.floor(availableWidth / itemWidth);
    const visibleCount = Math.min(maxVisible, allItems.length);
    
    if (visibleCount >= allItems.length) {
      setVisibleItems(allItems);
      setOverflowItems([]);
    } else {
      setVisibleItems(allItems.slice(0, visibleCount));
      setOverflowItems(allItems.slice(visibleCount));
    }
  }, [allItems]);

  // Recalculate on mount and resize
  useEffect(() => {
    calculateVisibleItems();
    window.addEventListener('resize', calculateVisibleItems);
    return () => window.removeEventListener('resize', calculateVisibleItems);
  }, [calculateVisibleItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOverflowOpen && overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setIsOverflowOpen(false);
      }
    };

    if (isOverflowOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          setIsOverflowOpen(false);
        }
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOverflowOpen]);

  return (
    <nav 
      ref={navRef}
      className={`flex items-center gap-1 ${className}`}
      style={{ maxWidth }}
    >
      {/* Visible items */}
      {visibleItems.map((item, index) => (
        <div key={index}>
          {item}
        </div>
      ))}

      {/* More button and overflow menu */}
      {overflowItems.length > 0 && (
        <div ref={overflowMenuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setIsOverflowOpen(!isOverflowOpen)}
            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-green-50 hover:text-green-600 transition-all duration-200 flex items-center gap-1"
          >
            More
            <svg 
              className={`w-4 h-4 transition-transform duration-200 ${isOverflowOpen ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown - positioned absolutely to parent */}
          {isOverflowOpen && (
            <div 
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '8px',
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px',
                minWidth: '200px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                zIndex: 9999
              }}
            >
              {overflowItems.map((item, index) => (
                <div key={index} style={{ marginBottom: index < overflowItems.length - 1 ? '4px' : '0' }}>
                  {React.cloneElement(item, {
                    className: "block w-full text-left px-3 py-2 rounded text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors",
                    onClick: () => {
                      // Close dropdown after navigation
                      setTimeout(() => setIsOverflowOpen(false), 100);
                    }
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

// Export NavItem for external use
export { NavItem };